import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen } from "lucide-react";
import SEO from "@/components/SEO";

const SORO_SRC =
  "https://app.trysoro.com/api/embed/6d38062f-d50d-4be5-b2d9-6b1c985c2927";

export default function Blog() {
  const [empty, setEmpty] = useState(false);
  const location = useLocation();
  // Canonical: /blog for the list, /blog?post=<slug> for a deep-linked article.
  const post = new URLSearchParams(location.search).get("post");
  const seoPath = post ? `/blog?post=${encodeURIComponent(post)}` : "/blog";

  useEffect(() => {
    // Re-inject the Soro embed script on every mount so the widget
    // initializes correctly also after SPA navigation.
    document
      .querySelectorAll(`script[src="${SORO_SRC}"]`)
      .forEach((s) => s.remove());
    const script = document.createElement("script");
    script.src = SORO_SRC;
    script.defer = true;
    document.body.appendChild(script);

    // Friendly notice if the widget renders nothing (e.g. embed disabled on Soro);
    // auto-hides if Soro populates the container later.
    const t = setTimeout(() => {
      const el = document.getElementById("soro-blog");
      if (el && el.childElementCount === 0) setEmpty(true);
    }, 7000);
    const target = document.getElementById("soro-blog");
    const observer = new MutationObserver(() => {
      if (target.childElementCount > 0) setEmpty(false);
    });
    if (target) observer.observe(target, { childList: true });

    return () => {
      clearTimeout(t);
      observer.disconnect();
      script.remove();
    };
  }, []);

  return (
    <div
      data-testid="blog-page"
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20"
    >
      <SEO
        path={seoPath}
        title="Blog — Notizie e consigli contro le truffe online"
        description="Il blog di ScudoNonni: articoli, notizie e consigli pratici per riconoscere ed evitare le truffe online. Aggiornamenti su SMS truffa, phishing, WhatsApp truffa e sicurezza per anziani."
      />
      <div className="text-center mb-12 animate-fade-up">
        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#22C55E]/15 text-[#16A34A] text-base font-bold mb-5">
          <BookOpen size={20} /> Blog
        </span>
        <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-[#1E3A8A] leading-tight">
          Notizie e consigli contro le truffe
        </h1>
        <p className="mt-5 text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
          Articoli aggiornati per restare sempre un passo avanti ai truffatori.
        </p>
      </div>

      <div id="soro-blog"></div>

      {empty && (
        <div
          data-testid="blog-empty-notice"
          className="bg-blue-50 border-4 border-[#1E3A8A]/15 rounded-[2rem] p-8 md:p-10 text-center"
        >
          <p className="text-xl md:text-2xl font-bold text-[#1E3A8A] mb-3">
            I primi articoli stanno arrivando
          </p>
          <p className="text-lg text-gray-700 max-w-xl mx-auto leading-relaxed">
            Il blog è in preparazione: torna a trovarci tra poco. Nel frattempo
            puoi leggere le nostre guide antitruffa nella sezione{" "}
            <a href="/guida" className="text-[#1E3A8A] font-bold underline">
              Guida Anti-Truffe
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}
